import type { OfflineStore } from './store';
import { randomBytes } from './temp-reference';
import type { PinRecord } from './types';

/**
 * The offline PIN of fuel-pda.md 13.4: 4–6 digits, checked on the device only
 * and stored as a salted PBKDF2-SHA256 hash per user. Five wrong PINs clear
 * the offline grant, so an online sign-in is needed again.
 */
export const PIN_PATTERN = /^\d{4,6}$/;
export const MAX_PIN_ATTEMPTS = 5;
export const PIN_ITERATIONS = 150_000;
const SALT_BYTES = 16;
const HASH_BITS = 256;

/** Arabic-Indic and Eastern Arabic-Indic digits are read as Latin digits. */
export function normalizePinDigits(value: string): string {
  return value.replace(/[٠-٩۰-۹]/g, (digit) => String((digit.codePointAt(0) ?? 0) & 0x0f));
}

export function isValidPin(pin: string): boolean {
  return PIN_PATTERN.test(pin);
}

function toBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCodePoint(byte);
  return btoa(binary);
}

function fromBase64(value: string): Uint8Array {
  return Uint8Array.from(atob(value), (char) => char.codePointAt(0) ?? 0);
}

/** Whether WebCrypto can derive the hash (it needs a secure context). */
export function pinCryptoAvailable(): boolean {
  return typeof globalThis.crypto?.subtle?.importKey === 'function';
}

export async function derivePinHash(
  pin: string,
  salt: Uint8Array,
  iterations: number,
): Promise<string> {
  const subtle = globalThis.crypto.subtle;
  const material = await subtle.importKey('raw', new TextEncoder().encode(pin), 'PBKDF2', false, [
    'deriveBits',
  ]);
  const bits = await subtle.deriveBits(
    { name: 'PBKDF2', hash: 'SHA-256', salt: salt as BufferSource, iterations },
    material,
    HASH_BITS,
  );
  return toBase64(new Uint8Array(bits));
}

/** A new record for `pin`; throws on a PIN that is not 4–6 digits. */
export async function createPinRecord(
  userId: string,
  pin: string,
  iterations: number = PIN_ITERATIONS,
): Promise<PinRecord> {
  if (!isValidPin(pin)) throw new RangeError('A PIN is 4 to 6 digits');
  const salt = randomBytes(SALT_BYTES);
  return {
    userId,
    salt: toBase64(salt),
    hash: await derivePinHash(pin, salt, iterations),
    iterations,
    failedAttempts: 0,
  };
}

/** Constant-time comparison of two base64 hashes of equal length. */
function sameHash(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let difference = 0;
  for (let i = 0; i < a.length; i += 1) difference |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return difference === 0;
}

export async function pinMatches(record: PinRecord, pin: string): Promise<boolean> {
  if (!isValidPin(pin)) return false;
  return sameHash(
    await derivePinHash(pin, fromBase64(record.salt), record.iterations),
    record.hash,
  );
}

export type UnlockResult =
  | { kind: 'OK' }
  | { kind: 'WRONG'; remaining: number }
  | { kind: 'LOCKED_OUT' }
  | { kind: 'NO_PIN' };

/**
 * Checks the PIN of `userId`. A right PIN resets the counter; the fifth wrong
 * one deletes the PIN and the offline grant (section 13.4).
 */
export async function unlockWithPin(
  store: OfflineStore,
  userId: string,
  pin: string,
): Promise<UnlockResult> {
  const record = await store.getPin(userId);
  if (!record) return { kind: 'NO_PIN' };
  if (await pinMatches(record, normalizePinDigits(pin))) {
    if (record.failedAttempts !== 0) await store.putPin({ ...record, failedAttempts: 0 });
    return { kind: 'OK' };
  }
  const failedAttempts = record.failedAttempts + 1;
  if (failedAttempts >= MAX_PIN_ATTEMPTS) {
    await store.deletePin(userId);
    await store.deleteValue('grant');
    return { kind: 'LOCKED_OUT' };
  }
  await store.putPin({ ...record, failedAttempts });
  return { kind: 'WRONG', remaining: MAX_PIN_ATTEMPTS - failedAttempts };
}
